/**
 * The shared Axios client.
 *
 * This module is deliberately thin: every decision it makes is delegated to a pure
 * module that can be property-tested without a network or a browser.
 *
 *   apiIdentity        header assembly and token query-param stripping (Req 1)
 *   httpRetryPolicy    401 / 403 / 429 policy and Retry-After parsing (Req 2)
 *   hmacSigner         request signing for mutating methods (Req 3)
 *   apiErrors          display-safe error descriptors (Req 2.3, 10)
 *   docsEndpointGuard  production block on /docs, /redoc, /openapi.json (Req 12)
 *
 * What stays here is sequencing and I/O: reading tokens, awaiting refreshes, sleeping
 * between rate-limited attempts, and re-dispatching a request.
 */

import axios from "axios";
import { tokenRefreshService } from "../services/tokenRefreshService";
import { performAppLogout } from "./authLogout";
import { getUserEmail, isSessionExpired } from "./userUtils";
import { TOKEN_SCOPE, resolveIdentityHeaders, stripTokenQueryParams } from "./apiIdentity";
import { assertDocsRequestAllowed } from "./docsEndpointGuard";
import { requiresSignature, signRequest } from "./hmacSigner";
import { RETRY_ACTION, RETRY_REASON, classifyRetry } from "./httpRetryPolicy";
import { normalizeApiError } from "./apiErrors";

const apiClient = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

const PROACTIVE_REFRESH_SKEW_MS = 60 * 1000;

/* ------------------------------------------------------------------ *
 * Endpoint classification (unchanged behaviour)
 * ------------------------------------------------------------------ */

function isAccessCheckEndpoint(url = "") {
  return (
    url.includes("admin/check-access") ||
    url.includes("supervisor/check-access") ||
    url.includes("semantic/check-access")
  );
}

function isDatabricksEndpoint(url = "") {
  const normalized = String(url || "").toLowerCase();
  if (isAccessCheckEndpoint(normalized)) return false;

  return (
    normalized.includes("/query") ||
    normalized.includes("query/") ||
    normalized.includes("execute-sql") ||
    normalized.includes("/supervisor/") ||
    normalized.includes("supervisor/") ||
    normalized.includes("pharma-dashboard") ||
    normalized.includes("sfe-dashboard") ||
    normalized.includes("efforts-dashboard") ||
    normalized.includes("field-sales") ||
    normalized.includes("executive-focus-note") ||
    normalized.includes("semantic/query") ||
    normalized.includes("semantic/thinking") ||
    normalized.includes("semantic/query-ai") ||
    normalized.includes("semantic/query-rule")
  );
}

/** Which token a URL needs. The classifier above is the single source of truth. */
function scopeForUrl(url) {
  return isDatabricksEndpoint(url) ? TOKEN_SCOPE.DATABRICKS : TOKEN_SCOPE.APP;
}

function isExpiringSoon(expiryMs, skewMs = PROACTIVE_REFRESH_SKEW_MS) {
  if (!expiryMs || !Number.isFinite(expiryMs)) return true;
  return Date.now() >= expiryMs - skewMs;
}

/* ------------------------------------------------------------------ *
 * Header and config helpers
 * ------------------------------------------------------------------ */

/** Merges headers onto a config, tolerating both plain objects and AxiosHeaders. */
function mergeHeaders(config, headers) {
  if (!config.headers) config.headers = {};
  const target = config.headers;

  for (const [name, value] of Object.entries(headers)) {
    if (typeof target.set === "function") target.set(name, value);
    else target[name] = value;
  }
}

/** Case-insensitive header read across plain objects and AxiosHeaders. */
function readResponseHeader(headers, name) {
  if (headers == null) return null;

  if (typeof headers.get === "function") {
    try {
      const value = headers.get(name);
      if (value != null) return value;
    } catch {
      /* fall through to the object scan */
    }
  }

  if (typeof headers !== "object") return null;

  const target = name.toLowerCase();
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === target) return headers[key];
  }
  return null;
}

/**
 * Acquires the token for a request's scope.
 *
 * Databricks-scoped URLs go through MSAL (refreshing proactively when the stored
 * token is close to expiry, as before); app-scoped URLs read the stored app token.
 */
async function acquireToken(scope) {
  if (scope === TOKEN_SCOPE.DATABRICKS) {
    const expiryRaw = localStorage.getItem("databricksTokenExpiry");
    const expiryMs = expiryRaw ? parseInt(expiryRaw, 10) : NaN;
    return tokenRefreshService.ensureDatabricksToken({
      forceRefresh: isExpiringSoon(expiryMs),
      interactive: true,
    });
  }

  return localStorage.getItem("appToken") || "";
}

/** True for a request the caller cancelled; an intentional abort is not a failure. */
function isAbortError(error) {
  if (error == null || typeof error !== "object") return false;
  if (axios.isCancel?.(error)) return true;
  return (
    error.name === "AbortError" ||
    error.name === "CanceledError" ||
    error.code === "ERR_CANCELED"
  );
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Attaches the display-safe descriptor and rejects.
 *
 * Every rejection out of this module carries `error.apiError`, so no consumer has to
 * re-parse a response body (Req 10.1, 10.2). Cancellations are additionally marked
 * `silent` so error surfaces can skip them.
 */
function reject(error, { silent = false } = {}) {
  if (error != null && typeof error === "object") {
    error.apiError = normalizeApiError(error);
    if (silent) error.silent = true;
  }
  return Promise.reject(error);
}

/* ------------------------------------------------------------------ *
 * Request interceptor (Req 1, 3, 12)
 * ------------------------------------------------------------------ */

apiClient.interceptors.request.use(async (config) => {
  if (isSessionExpired()) {
    await tokenRefreshService.enforceSessionLogout();
    return reject(new Error("Session expired after 24 hours. Please sign in again."));
  }

  // Before any other work, so a stray codegen helper cannot reach production docs.
  assertDocsRequestAllowed(config.url);

  const scope = scopeForUrl(config.url || "");
  const token = await acquireToken(scope);

  // One place builds identity headers for every request, so X-User-Email is no
  // longer limited to the semantic endpoints (Req 1.1, 1.3, 1.4).
  mergeHeaders(config, resolveIdentityHeaders({ token, email: getUserEmail(), scope }));

  // Regression guard: nothing in the app puts a token in the query string today,
  // and this keeps it that way (Req 1.2).
  const stripped = stripTokenQueryParams(config.url, config.params);
  config.url = stripped.url;
  config.params = stripped.params;

  // Signing runs last so the hash covers the final URL path and body (Req 3.1, 3.7).
  if (requiresSignature(config.method)) {
    await signRequest(config);
  }

  return config;
}, reject);

/* ------------------------------------------------------------------ *
 * Response interceptor (Req 2, 10)
 * ------------------------------------------------------------------ */

/**
 * Refreshes the token for a scope.
 *
 * Both refresh paths write the new token to localStorage, so the retry picks it up
 * through the request interceptor rather than by patching headers here.
 *
 * @returns {Promise<boolean>} whether a usable token was obtained
 */
async function refreshTokenForScope(scope) {
  if (scope === TOKEN_SCOPE.DATABRICKS) {
    const token = await tokenRefreshService.ensureDatabricksToken({
      forceRefresh: true,
      interactive: true,
    });
    return Boolean(token);
  }

  const result = await tokenRefreshService.refreshAppToken({ forceRefresh: true });
  return Boolean(result?.appToken);
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isAbortError(error)) return reject(error, { silent: true });

    const originalRequest = error.config;
    if (!originalRequest) return reject(error);

    const decision = classifyRetry({
      status: error.response?.status,
      attempts: originalRequest._rateLimitRetries,
      hasAuthRetried: originalRequest._authRetried === true,
      retryAfter: readResponseHeader(error.response?.headers, "retry-after"),
    });

    // 401, first occurrence: refresh once and replay. This now applies to every
    // authenticated request, not only Databricks-scoped ones (Req 2.1).
    if (decision.action === RETRY_ACTION.REFRESH_RETRY) {
      originalRequest._authRetried = true;

      let refreshed = false;
      try {
        refreshed = await refreshTokenForScope(scopeForUrl(originalRequest.url || ""));
      } catch {
        refreshed = false;
      }

      if (!refreshed) {
        // No usable token means the session is over: land the user on the login
        // route rather than leaving a half-authenticated screen (Req 2.2).
        await performAppLogout();
        return reject(error);
      }

      return apiClient(originalRequest);
    }

    // 429 within the retry ceiling: wait out Retry-After (or the 5s default) and
    // replay, at most three times (Req 2.4-2.6).
    if (decision.action === RETRY_ACTION.WAIT_RETRY) {
      originalRequest._rateLimitRetries = (originalRequest._rateLimitRetries || 0) + 1;
      await sleep(decision.delayMs);
      return apiClient(originalRequest);
    }

    // A 401 that survives the one allowed refresh means the new token is not
    // accepted either, so the session is over (Req 2.2).
    if (decision.reason === RETRY_REASON.UNAUTHORIZED) {
      await performAppLogout();
      return reject(error);
    }

    // Everything else fails here, including 403, which never triggers a refresh
    // and is surfaced as "Access Denied" by normalizeApiError (Req 2.3).
    return reject(error);
  }
);

export default apiClient;
